import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getPublicMarketingConfig } from "@/services/marketing";

/**
 * Injects Google Analytics and Facebook Pixel scripts on public pages only.
 * Must be rendered inside a <BrowserRouter>.
 */
export default function TrackingScripts() {
  const location = useLocation();
  const injectedRef = useRef<{ ga?: string; gtm?: string; fb?: string }>({});

  // Don't inject on admin routes
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;

    let cancelled = false;

    (async () => {
      try {
        const config = await getPublicMarketingConfig();
        if (cancelled || !config) return;

        // ── Google Analytics ──
        const gaId = config.google_analytics_id;
        if (gaId && injectedRef.current.ga !== gaId) {
          // Remove previous if ID changed
          document.querySelectorAll("[data-tracking='ga']").forEach((el) => el.remove());

          const script1 = document.createElement("script");
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
          script1.setAttribute("data-tracking", "ga");
          document.head.appendChild(script1);

          const script2 = document.createElement("script");
          script2.setAttribute("data-tracking", "ga");
          script2.textContent = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', ${JSON.stringify(gaId)});
          `;
          document.head.appendChild(script2);

          injectedRef.current.ga = gaId;
        } else if (!gaId && injectedRef.current.ga) {
          document.querySelectorAll("[data-tracking='ga']").forEach((el) => el.remove());
          injectedRef.current.ga = undefined;
        }

        // ── Google Tag Manager ──
        const gtmId = (config as any).google_tag_manager_id as string | null;
        if (gtmId && injectedRef.current.gtm !== gtmId) {
          document.querySelectorAll("[data-tracking='gtm']").forEach((el) => el.remove());

          const gtmScript = document.createElement("script");
          gtmScript.setAttribute("data-tracking", "gtm");
          gtmScript.textContent = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer',${JSON.stringify(gtmId)});
          `;
          document.head.appendChild(gtmScript);

          // noscript iframe fallback in body
          const gtmNoscript = document.createElement("noscript");
          gtmNoscript.setAttribute("data-tracking", "gtm");
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.display = "none";
          iframe.style.visibility = "hidden";
          gtmNoscript.appendChild(iframe);
          document.body.insertBefore(gtmNoscript, document.body.firstChild);

          injectedRef.current.gtm = gtmId;
        } else if (!gtmId && injectedRef.current.gtm) {
          document.querySelectorAll("[data-tracking='gtm']").forEach((el) => el.remove());
          injectedRef.current.gtm = undefined;
        }

        // ── Facebook Pixel ──
        const fbId = config.facebook_pixel_id;
        if (fbId && injectedRef.current.fb !== fbId) {
          document.querySelectorAll("[data-tracking='fb']").forEach((el) => el.remove());

          const fbScript = document.createElement("script");
          fbScript.setAttribute("data-tracking", "fb");
          fbScript.textContent = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${JSON.stringify(fbId)});
            fbq('track', 'PageView');
          `;
          document.head.appendChild(fbScript);

          // noscript fallback in body
          const noscript = document.createElement("noscript");
          noscript.setAttribute("data-tracking", "fb");
          const img = document.createElement("img");
          img.height = 1;
          img.width = 1;
          img.style.display = "none";
          img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(fbId)}&ev=PageView&noscript=1`;
          noscript.appendChild(img);
          document.body.appendChild(noscript);

          injectedRef.current.fb = fbId;
        } else if (!fbId && injectedRef.current.fb) {
          document.querySelectorAll("[data-tracking='fb']").forEach((el) => el.remove());
          injectedRef.current.fb = undefined;
        }
      } catch {
        // Silently fail — tracking is non-critical
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  return null;
}
